import React, { useCallback, useEffect, useLayoutEffect, useReducer, useRef, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import SettingsRoundedIcon from '@mui/icons-material/SettingsRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import VolumeUpRoundedIcon from '@mui/icons-material/VolumeUpRounded';
import ClassroomTrainEngineArt from './ClassroomTrainEngineArt';
import { classroomTrainLayout } from './classroomTrainLayout';
import ClassroomSchoolLetter from './ClassroomSchoolLetter';
import { nextTrainSession, trainAttempt, trainReducer } from './continuousTrain';
import { playMiniTaskSound } from '../soundEffects';
import { setPronunciationPlayback } from './pronunciationPlayback';
import './memoryTrain.css';
import './premiumTrain.css';
import './classroomTrain.css';
import './classroomTrainFidelityRails.css';
import './classroomTrainFidelityScene.css';
import './classroomTrainCompactScale.css';
import './classroomTrainResponsiveScale.css';
import './classroomTrainBackdrop.css';

export default function ClassroomTrain({ data, pendingData, paused = false, onSettings, onClose }) {
  const [state, dispatch] = useReducer(trainReducer, data, value => trainAttempt(nextTrainSession(null, value)));
  const { session, tiles, slots, phase } = state;
  const [dragOffset, setDragOffset] = useState(null);
  const stage = useRef(null), drag = useRef(null), suppressClick = useRef(false);
  const first = useRef(true), latest = useRef(data);
  latest.current = pendingData || data;
  const active = session?.target;
  const [layout, setLayout] = useState(() => classroomTrainLayout(
    typeof window === 'undefined' ? 1280 : window.innerWidth,
    typeof window === 'undefined' ? 900 : window.innerHeight,
    slots.length,
  ));
  const [stageElement, setStageElement] = useState(null);
  const bindStage = useCallback(element => { stage.current = element; setStageElement(element); }, []);

  useLayoutEffect(() => {
    if (!stageElement) return undefined;
    const measure = () => {
      const width = stageElement.clientWidth || stageElement.getBoundingClientRect().width;
      if (width <= 0) return; // JSDOM, hidden preview or transient dialog.
      const next = classroomTrainLayout(width, window.innerHeight, slots.length);
      setLayout(previous => Object.keys(next).every(key => previous[key] === next[key]) ? previous : next);
    };
    measure(); // layout effect: settle geometry before the first visible frame.
    const observer = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    observer?.observe(stageElement);
    window.addEventListener('resize', measure);
    window.visualViewport?.addEventListener('resize', measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener('resize', measure);
      window.visualViewport?.removeEventListener('resize', measure);
    };
  }, [stageElement, slots.length]);

  useEffect(() => {
    if (!session && !paused) {
      const next = nextTrainSession(null, latest.current);
      if (next) dispatch({ type: 'reset', session: next });
    }
  }, [session, pendingData, paused]);
  useEffect(() => {
    if (paused) { drag.current = null; setDragOffset(null); }
  }, [paused]);
  useEffect(() => {
    if (paused || !active || phase !== 'speaking') return undefined;
    let live = true, sound, watchdog, delay;
    const fail = () => { if (live) { sound?.pause(); dispatch({ type: 'phase', phase: 'retry' }); } };
    const announce = () => {
      if (!live) return;
      first.current = false;
      try {
        sound = new Audio(); sound.src = active.audio.url;
        setPronunciationPlayback(sound);
        sound.onended = () => { if (live) dispatch({ type: 'phase', phase: 'ready' }); };
        sound.onerror = fail; watchdog = setTimeout(fail, 15000);
        Promise.resolve(sound.play()).catch(fail);
      } catch (_) { fail(); }
    };
    if (first.current) delay = setTimeout(announce, 1000); else announce();
    return () => {
      live = false; clearTimeout(delay); clearTimeout(watchdog);
      if (sound) { sound.pause(); sound.onended = null; sound.onerror = null; }
    };
  }, [active, phase, paused]);
  useEffect(() => {
    if (paused || !['correct', 'wrong'].includes(phase)) return undefined;
    let live = true, timer, watchdog, voice, fallbackStarted = false;
    const finish = () => {
      if (!live) return;
      if (phase === 'wrong') dispatch({ type: 'wrong-reset' });
      else dispatch({ type: 'reset', session: nextTrainSession(session, latest.current) });
    };
    const fallback = () => {
      if (!live || fallbackStarted) return;
      fallbackStarted = true;
      clearTimeout(watchdog);
      voice?.pause();
      if (voice) { voice.onended = null; voice.onerror = null; }
      // A missing/broken approved clip must never block the class from retrying.
      playMiniTaskSound('wrong', true);
      timer = setTimeout(finish, 650);
    };
    if (phase === 'correct') {
      playMiniTaskSound('correct', true);
      timer = setTimeout(finish, 1100);
    } else {
      // Only the exact clip personally approved for the teacher train is accepted.
      const recording = latest.current?.train_feedback || data?.train_feedback;
      const approved = recording?.id === 'tr_g1_l1_g08_wrong'
        && recording?.sha256 === '22c75731037642df735783f988f4742b9dc37b13a5a4861f661be2a0a8a3d817'
        && typeof recording?.url === 'string' && recording.url.startsWith('https://');
      if (!approved) fallback();
      else {
        try {
          voice = new Audio();
          voice.src = recording.url;
          voice.onended = () => {
            if (fallbackStarted) return;
            fallbackStarted = true;
            clearTimeout(watchdog);
            finish(); // The same target is announced only after Tomris finishes.
          };
          voice.onerror = fallback;
          watchdog = setTimeout(fallback, 15000);
          Promise.resolve(voice.play()).catch(fallback);
        } catch (_) { fallback(); }
      }
    }
    return () => {
      live = false; clearTimeout(timer); clearTimeout(watchdog);
      if (voice) { voice.pause(); voice.onended = null; voice.onerror = null; }
    };
  }, [phase, paused, session, data]);
  const wasPaused = useRef(paused);
  useEffect(() => {
    if (wasPaused.current && !paused && ['ready', 'retry'].includes(phase)) dispatch({ type: 'phase', phase: 'speaking' });
    wasPaused.current = paused;
  }, [paused, phase]);

  const ready = !paused && phase === 'ready';
  const place = (id, index) => { if (ready) dispatch({ type: 'place', id, index }); };
  const pointerDown = (event, id) => {
    if (!ready || slots.includes(id) || (event.button !== undefined && event.button !== 0)) return;
    suppressClick.current = false;
    drag.current = { pointer: event.pointerId, id, x: event.clientX, y: event.clientY, moved: false };
    event.currentTarget.setPointerCapture?.(event.pointerId);
  };
  const pointerMove = event => {
    const current = drag.current;
    if (!current || current.pointer !== event.pointerId) return;
    const x = event.clientX - current.x, y = event.clientY - current.y;
    if (Math.hypot(x, y) > 8) current.moved = true;
    if (current.moved) setDragOffset({ id: current.id, x, y });
  };
  const pointerUp = event => {
    const current = drag.current;
    if (!current || current.pointer !== event.pointerId) return;
    drag.current = null; setDragOffset(null);
    if (current.moved) {
      suppressClick.current = true;
      const wagon = document.elementFromPoint(event.clientX, event.clientY)?.closest('[data-classroom-wagon]');
      if (wagon && stage.current?.contains(wagon)) place(current.id, Number(wagon.dataset.classroomWagon));
    }
  };
  const rowCount = Math.ceil(slots.length / layout.columns), perRow = Math.ceil(slots.length / (rowCount || 1));
  const responsiveScale = {
    '--teacher-wagon-width': `${layout.wagonWidth}px`,
    '--teacher-wagon-height': `${layout.wagonHeight}px`,
    '--teacher-engine-width': `${layout.engineWidth}px`,
    '--teacher-train-gap': `${layout.gap}px`,
  };
  const rows = Array.from({ length: rowCount }, (_, row) => slots.slice(row * perRow, (row + 1) * perRow));
  const status = paused ? 'Etkinlik duraklatıldı.' : phase === 'correct' ? '✓ Harika! Tren yola çıkıyor.'
    : phase === 'wrong' ? '✕ Birlikte tekrar deneyelim. Aynı hedefi dinleyelim.'
      : phase === 'speaking' ? '' : phase === 'retry' ? 'Ses çalınamadı. Yeniden dinlemeyi deneyin.'
        : 'Harfleri sırayla vagonlara yerleştir.';
  // data-short-target is a legacy *art mode*, not the length of the word.
  // Retire the mismatched image-sprite wagons: 2, 3 and 4+ letters all use the
  // same panda locomotive, CSS-drawn wagons/wheels and long-word rails.
  return <Dialog open fullScreen transitionDuration={0} fullWidth maxWidth={false} onClose={paused ? undefined : onClose} aria-labelledby="classroom-train-title"
    PaperProps={{ className: 'classroom-train-modal' }}>
    <Box className="classroom-train hece-premium" data-phase={phase} data-paused={String(paused)} data-short-target="false" data-target-length={slots.length}
      data-train-columns={layout.columns} data-multi-row={String(rowCount > 1)} style={responsiveScale}>
      <header className="classroom-train-header"><div className="classroom-train-title-group"><Typography component="h2" id="classroom-train-title">Hece Treni</Typography><Typography component="p" className="classroom-train-subtitle">Harfleri sırayla vagonlara yerleştir!</Typography></div>
        <div><Button aria-label="Öğrenilen Harfler" startIcon={<SettingsRoundedIcon />} disabled={paused} onClick={onSettings}>Ayarlar</Button>
          <Button startIcon={<CloseRoundedIcon />} disabled={paused} onClick={onClose}>Kapat</Button></div>
      </header>
      {!session ? <section className="classroom-train-empty"><ClassroomTrainEngineArt /><Typography component="h3">Trene harflerimizi hazırlayalım</Typography>
        <Typography>Öğrenilen harflerle uygun, onaylı sesli hece veya kelime henüz yok. Harfleri birlikte kontrol edelim.</Typography>
        <Button variant="contained" disabled={paused} onClick={onSettings}>Öğrenilen Harfler</Button></section> : <>
        <section className="classroom-train-playarea" aria-label="Hece Treni oyun alanı">
        <div ref={bindStage} className={`classroom-train-stage ${phase === 'correct' ? 'is-departing' : ''}`} role="group" aria-label="Sıralı vagonlar">
          <div className="classroom-train-rows">{rows.map((rowSlots, row) => <div className="classroom-train-row" key={row}>
            <div className="classroom-train-rolling-stock">
            <div className="classroom-train-wagons" style={{
              '--row-wagons': rowSlots.length,
              '--teacher-row-width': `${rowSlots.length * layout.wagonWidth + (rowSlots.length - 1) * layout.gap}px`,
            }}>
              {rowSlots.map((id, column) => {
                const index = row * perRow + column, tile = tiles.find(item => item.id === id);
                return <button key={index} type="button" data-classroom-wagon={index} disabled={!ready}
                  aria-label={tile ? `${index + 1}. vagon, ${tile.letter}, boşalt` : `${index + 1}. boş vagon`}
                  className={`train-wagon ${id ? 'is-filled' : ''} ${index === slots.indexOf(null) ? 'is-next' : ''}`}
                  onClick={() => dispatch({ type: 'clear', index })}>
                  <span className="train-window">{tile ? <ClassroomSchoolLetter letter={tile.letter} /> : <span className="wagon-number" aria-hidden="true">{index + 1}</span>}</span>
                  <span className="wagon-wheels" aria-hidden="true"><i /><i /></span>
                </button>;
              })}
            </div>{row === 0 && <div className="classroom-train-engine"><ClassroomTrainEngineArt mockup /></div>}
            </div>
            <div className="train-track" aria-hidden="true" />
          </div>)}</div>
        </div>
        <div className={`classroom-train-controls ${slots.length > 4 ? 'is-long' : ''}`}>
        <Typography className="classroom-train-cue">Dokun veya taşı · Düzeltmek için dolu vagona dokun</Typography>
        <div className="train-stones" role="group" aria-label="Harf taşları">{tiles.map((tile, trayIndex) => <button key={tile.id} type="button"
          data-train-tile={tile.id} aria-label={`${tile.letter} harfini yerleştir, ${trayIndex + 1}. taş`}
          disabled={!ready || slots.includes(tile.id)} className={`train-stone ${slots.includes(tile.id) ? 'is-used' : ''} ${dragOffset?.id === tile.id ? 'is-dragging' : ''}`}
          style={dragOffset?.id === tile.id ? { transform: `translate(${dragOffset.x}px, ${dragOffset.y}px)` } : undefined}
          onPointerDown={event => pointerDown(event, tile.id)} onPointerMove={pointerMove} onPointerUp={pointerUp}
          onPointerCancel={() => { drag.current = null; suppressClick.current = true; setDragOffset(null); }}
          onClick={event => { const suppressed = suppressClick.current && event.detail > 0; suppressClick.current = false; if (!suppressed) place(tile.id); }}>
          <ClassroomSchoolLetter letter={tile.letter} /></button>)}</div>
        <div className="classroom-train-listen"><Button className="train-listen" aria-label="Hedefi yeniden dinle"
          disabled={paused || !['ready', 'retry'].includes(phase)} onClick={() => dispatch({ type: 'phase', phase: 'speaking' })}><VolumeUpRoundedIcon /></Button>
          <Typography>Dinle, harfleri vagonlara taşı</Typography></div>
        {phase === 'retry' && <Alert severity="error">Hedef sesi duyulmadan harfler açılmaz. Hoparlör düğmesiyle yeniden deneyin.</Alert>}
        <Typography role="status" aria-live="polite" className={`classroom-train-status ${phase}`}>{status}</Typography>
        </div>
        </section>
      </>}
    </Box>
  </Dialog>;
}
